import type { ActivationRule, FormQuestion } from "../client/generated";

interface BaseInput {
  key: string;
  label: string;
  required: boolean;
  activationRule?: ActivationRule;
  placeholder?: string;
  title?: string;
}

interface TextInput extends BaseInput {
  type: "text";
  value: string;
}
interface NumberInput extends BaseInput {
  type: "number";
  value: string;
  min?: number;
  max?: number;
}
interface EmailInput extends BaseInput {
  type: "email";
  value: string;
  sharing_allowed: boolean;
}
export interface SelectInput extends BaseInput {
  type: "select";
  options: { label: string; value: string }[];
  value: string;
}
interface DateInput extends BaseInput {
  type: "date";
  value: string;
  pattern: string;
}

export type QuestionDictoinary = { [key: string]: TextInput | NumberInput | SelectInput | EmailInput | DateInput };

export function checkIfQuestionShouldBeHidden(questions: QuestionDictoinary, key: string): boolean {
  const question = questions[key];
  if (!question) throw new Error("question not found");
  const { activationRule: rule } = question;
  if (rule?.type === "==" && rule?.question_id !== undefined) {
    const refQuestion = questions[String(rule.question_id)];
    if (!refQuestion) throw new Error("ref question not found");
    return refQuestion.value === rule.ref_value?.toString();
  }
  return true;
}

export function getQuestionDictionary(questions: FormQuestion[]) {
  const questionDict: QuestionDictoinary = {};
  for (const question of questions) {
    switch (question.type) {
      case "CHOICE":
        questionDict[question.id] = {
          key: question.id.toString(),
          label: question.question,
          type: "select",
          options: question.choices?.map((choice) => ({ label: choice.label, value: choice.value })) ?? [],
          value: question.response ?? (question.required ? (question.choices?.[0]?.value ?? "") : ""),
          required: question.required,
          activationRule: question.rule,
        };
        break;
      case "STRING":
        questionDict[question.id] = {
          key: question.id.toString(),
          label: question.question,
          type: question.id === "email" ? "email" : "text",
          value: question.response ?? "",
          required: question.required,
          activationRule: question.rule,
          placeholder: { dob: "DD.MM.YYYY", email: "E-Mail Adresse" }[question.id],
          sharing_allowed: false,
          title: question.id === "dob" ? "Bitte ein Datum im Format DD.MM.YYYY angeben" : undefined,
        };
        break;
      case "DATE":
        questionDict[question.id] = {
          key: question.id.toString(),
          label: question.question,
          type: "date",
          value: question.response ?? "",
          required: question.required,
          activationRule: question.rule,
          placeholder: "DD.MM.YYYY",
          pattern: "\\d{1,2}\\.\\d{1,2}\\.\\d{4}",
          title: "Bitte ein Datum im Format DD.MM.YYYY angeben",
        };
        break;
      case "INT":
        questionDict[question.id] = {
          key: question.id.toString(),
          label: question.question,
          type: "number",
          value: question.response ?? "",
          required: question.required,
          activationRule: question.rule,
          min: question.id === "dob" ? 1920 : undefined,
          max: question.id === "dob" ? new Date().getFullYear() : undefined,
          title: question.id === "dob" ? "Bitte ein Geburtsjahr zwischen 1920 und 2010 angeben" : undefined,
        };
        break;
    }
  }
  return questionDict;
}
